import { cn } from '@/lib/utils';
import type { SupportUserRef } from '@/lib/api';

interface UserIdentityProps {
  /** Enriched identity from the support admin DTO (preferred). */
  user?: SupportUserRef | null;
  /** Raw id fallback used when enrichment is unavailable. */
  userId?: string | null;
  /** Render the raw id as a small secondary line under the name (default true). */
  showId?: boolean;
  className?: string;
}

/**
 * Canonical way to render a customer in the support admin backend: a
 * human-readable name first (displayName → username), with the opaque `usr_*`
 * id demoted to a small, truncated secondary line. Used everywhere a support
 * submission's owner is shown so admins never have to parse raw ids.
 *
 * Truncation (`min-w-0` + `truncate`) keeps long ids from overflowing their
 * container — the bug where the id escaped the customer-context sidebar.
 */
export function UserIdentity({ user, userId, showId = true, className }: UserIdentityProps) {
  const id = user?.id ?? userId ?? null;
  const name = user?.displayName ?? user?.username ?? null;
  const primary = name ?? id ?? 'Unknown user';
  const showSecondary = showId && id !== null && name !== null;

  return (
    <div className={cn('min-w-0', className)}>
      <p className="truncate text-sm font-medium" title={primary}>
        {primary}
      </p>
      {showSecondary && (
        <p className="truncate font-mono text-[10px] text-muted-foreground" title={id ?? undefined}>
          {id}
        </p>
      )}
    </div>
  );
}
